import { useEffect, useState } from 'react';
import { peopleGateway } from '../../index';
import { US_STATE_CODES, type AddressInput, type AddressValue, type EmployeeAddressResponse, type EmployeeResponse } from '../../types';

interface FormAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

const EMPTY: FormAddress = { line1: '', line2: '', city: '', state: '', zip: '' };

function toForm(a: AddressValue | null | undefined): FormAddress {
  return a ? { line1: a.addressLine1, line2: a.addressLine2 ?? '', city: a.city, state: a.state, zip: a.zip } : EMPTY;
}

function toInput(f: FormAddress): AddressInput {
  return { addressLine1: f.line1.trim(), addressLine2: f.line2.trim() || null, city: f.city.trim(), state: f.state, zip: f.zip.trim(), country: 'US' };
}

function AddressFields({ legend, value, onChange }: { legend: string; value: FormAddress; onChange: (next: FormAddress) => void }) {
  const set = (patch: Partial<FormAddress>) => onChange({ ...value, ...patch });
  return (
    <fieldset className="profile-address-fieldset">
      <legend>{legend}</legend>
      <div className="profile-field-grid">
        <label>
          {legend}: Address Line 1
          <input value={value.line1} maxLength={200} onChange={(e) => set({ line1: e.target.value })} required />
        </label>
        <label>
          {legend}: Address Line 2
          <input value={value.line2} maxLength={200} onChange={(e) => set({ line2: e.target.value })} />
        </label>
        <label>
          {legend}: City
          <input value={value.city} maxLength={100} onChange={(e) => set({ city: e.target.value })} required />
        </label>
        <label>
          {legend}: State
          <select value={value.state} onChange={(e) => set({ state: e.target.value })} required>
            <option value="">Select a state</option>
            {US_STATE_CODES.map((code) => (
              <option key={code} value={code}>{code}</option>
            ))}
          </select>
        </label>
        <label>
          {legend}: ZIP Code
          <input value={value.zip} maxLength={10} placeholder="12345 or 12345-6789" onChange={(e) => set({ zip: e.target.value })} required />
        </label>
        <label>
          {legend}: Country
          <input value="United States" disabled />
        </label>
      </div>
    </fieldset>
  );
}

/** Home/Residence Address and the optional Mailing Address (EPN-153, slice 2 of EPN-149), from the approved SRS. The server
 * decides what is valid (required fields, ZIP format, state code, US only, effective date rules). County and local tax area
 * are not shown: they are filled in automatically once an approved lookup source exists. */
export function EmployeeAddressSection({ employee }: { employee: EmployeeResponse }) {
  const [current, setCurrent] = useState<EmployeeAddressResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [home, setHome] = useState<FormAddress>(EMPTY);
  const [effectiveDate, setEffectiveDate] = useState('');
  const [hasMailing, setHasMailing] = useState(false);
  const [mailing, setMailing] = useState<FormAddress>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  function apply(r: EmployeeAddressResponse) {
    setCurrent(r);
    setHome(toForm(r.home?.address));
    setEffectiveDate('');
    setHasMailing(r.mailing !== null);
    setMailing(toForm(r.mailing));
  }

  useEffect(() => {
    let cancelled = false;
    void peopleGateway
      .getEmployeeAddress(employee.id)
      .then((r) => {
        if (!cancelled) apply(r);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the address.');
      });
    return () => {
      cancelled = true;
    };
  }, [employee.id]);

  const cur = current?.home?.address;
  const homeChanges =
    cur !== undefined &&
    (cur.addressLine1 !== home.line1.trim() || (cur.addressLine2 ?? '') !== home.line2.trim() || cur.city !== home.city.trim() ||
      cur.state !== home.state || cur.zip !== home.zip.trim());

  async function save() {
    setError(null);
    setSaved(false);
    if (cur && homeChanges && !effectiveDate) {
      setError('Please enter an effective date for the home address change before saving.');
      return;
    }
    setSaving(true);
    try {
      const result = await peopleGateway.saveEmployeeAddress(employee.id, {
        home: toInput(home),
        effectiveDate: cur && homeChanges ? effectiveDate : null,
        mailing: hasMailing ? toInput(mailing) : null,
      });
      apply(result);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the address.');
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <section className="profile-section">
        <h3 className="profile-section-title">Home/Residence Address</h3>
        <p className="error">{loadError}</p>
      </section>
    );
  }

  return (
    <section className="profile-section">
      <h3 className="profile-section-title">Home/Residence Address</h3>
      <p className="muted">
        The legal residential address. It is stored separately from the work location and is used to determine the employee&apos;s residence state for payroll tax.
      </p>
      <AddressFields legend="Home Address" value={home} onChange={setHome} />

      {cur && homeChanges && (
        <label style={{ display: 'block', marginTop: 12 }}>
          Effective Date
          <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} required />
        </label>
      )}

      <label style={{ marginTop: 16 }}>
        <input type="checkbox" checked={!hasMailing} onChange={(e) => setHasMailing(!e.target.checked)} /> Mailing address same as residence
      </label>
      {hasMailing && (
        <>
          <p className="muted">The mailing address is for mail only. It is never used for payroll tax.</p>
          <AddressFields legend="Mailing Address" value={mailing} onChange={setMailing} />
        </>
      )}

      {error && <p className="error">{error}</p>}
      {saved && <p className="muted" role="status">Address saved.</p>}
      <div style={{ marginTop: 12 }}>
        <button type="button" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save Address'}
        </button>
      </div>

      <h4 style={{ marginTop: 20 }}>Home Address History</h4>
      {current && current.homeHistory.length > 0 ? (
        <table className="profile-history-table">
          <thead>
            <tr><th>Address</th><th>Effective</th><th>Changed</th></tr>
          </thead>
          <tbody>
            {current.homeHistory.map((h) => (
              <tr key={h.effectiveFrom}>
                <td>
                  {h.address.addressLine1}{h.address.addressLine2 ? `, ${h.address.addressLine2}` : ''}, {h.address.city}, {h.address.state} {h.address.zip}
                  {h.isCurrent ? ' (current)' : ''}
                </td>
                <td>{h.effectiveFrom}</td>
                <td>{h.changedAtUtc.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">No home address has been saved yet.</p>
      )}
    </section>
  );
}
